import React, { useState, useEffect, useRef } from 'react';

import { makeStyles } from 'design-system/product/theme';

import { LabelChoice } from '@scale/shared/types/params/annotation';
import AttributeShortcutInput from 'react-scale/components/scalers/common/AnnotationAttributeInput/AttributeShortcutInput';

interface AttributeShortcutSelectWrapperProps {
  attrKey: string;
  attrParams: $FIXME;
  selectedChoices?: string[];
  isReadOnly?: boolean;
  handleSelect: (choices: string[]) => void;
}

const useActiveElement = () => {
  const [active, setActive] = useState(document.activeElement);

  const handleFocusIn = () => {
    setActive(document.activeElement);
  };

  useEffect(() => {
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  return active;
};

const AttributeShortcutSelectWrapper: React.FC<AttributeShortcutSelectWrapperProps> = ({
  attrKey,
  attrParams,
  selectedChoices,
  isReadOnly,
  handleSelect,
}) => {
  const classes = useStyles();

  const active = useActiveElement();
  const ref = useRef<HTMLDivElement>(null);

  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (active === ref.current && !isFocused) {
      setIsFocused(true);
    } else if (active !== ref.current && isFocused) {
      // console.log('unfocusing now');
      setIsFocused(false);
    }
  }, [active, isFocused]);

  const handleSingleSelect = (choice: string) => {
    if (!selectedChoices) {
      handleSelect([choice]);
    } else if (selectedChoices.includes(choice)) {
      handleSelect(selectedChoices.filter(c => c !== choice));
    } else {
      handleSelect([...selectedChoices, choice]);
    }
  };

  return (
    <div className={classes.inputGrouping} key={attrKey} tabIndex={0} ref={ref}>
      {attrParams.choices &&
        attrParams.choices.map((c: LabelChoice, idx: number) => {
          return (
            <AttributeShortcutInput
              key={(typeof c !== 'string' && c.choice) || (c as string)}
              choice={c}
              selectedChoices={selectedChoices ?? []}
              isReadOnly={isReadOnly}
              handleSelect={handleSingleSelect}
              index={idx}
              isFocused={isFocused}
            />
          );
        })}
    </div>
  );
};

const useStyles = makeStyles({
  inputGrouping: {
    '&:focus': {
      borderLeft: '4px solid blue',
    },
  },
});

export default AttributeShortcutSelectWrapper;
